import React, { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fmt, useApi, useLoad } from "../lib/api";
import { Loading } from "./kit";

export function StarRow({ value, onPick, idPrefix }: { value: number; onPick?: (n: number) => void; idPrefix: string }) {
  return (
    <div className="flex gap-1" role={onPick ? "radiogroup" : "img"} aria-label={onPick ? "Choose 1 to 5 stars" : `${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const icon = <Star className={`h-6 w-6 ${n <= value ? "fill-[var(--red)] text-[var(--red)]" : "text-[var(--steel)]"}`} />;
        return onPick ? (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n > 1 ? "s" : ""}`} onClick={() => onPick(n)}
            className="rounded p-1 transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-[var(--blue)]" data-testid={`${idPrefix}-star-${n}`}>{icon}</button>
        ) : <span key={n}>{icon}</span>;
      })}
    </div>
  );
}

function Theirs({ v, side, onReport }: { v: any; side: string; onReport: (id: string) => void }) {
  if (v.their_review) {
    return (
      <div className="flex flex-wrap items-center gap-3" data-testid="rating-their-review">
        <span className="text-sm font-semibold">Their rating:</span>
        {v.their_review.stars ? <StarRow value={v.their_review.stars} idPrefix="their" /> : <span className="badge">hidden by moderation</span>}
        {v.their_review.reported ? <span className="badge badge-blue">reported</span> : (
          <button className="btn btn-ghost btn-sm" onClick={() => onReport(v.their_review.id)} data-testid="rating-report-button">Report</button>
        )}
      </div>
    );
  }
  if (v.their_review_pending) return <p className="text-xs text-[var(--muted)]" data-testid="rating-blind-note">The {side === "customer" ? "provider" : "customer"} has rated. Ratings stay hidden until you rate too, or until {fmt(v.reveal_at)}.</p>;
  return null;
}

export function RatingPanel({ side, introId, title }: { side: "customer" | "vendor"; introId: string; title?: string }) {
  const api = useApi();
  const base = `/${side}/introductions/${introId}`;
  const { data: v, error, reload } = useLoad<any>(`${base}/rating`);
  const [pick, setPick] = useState(0);
  const [busy, setBusy] = useState(false);
  const run = async (method: string, path: string, body?: any, ok?: string) => {
    setBusy(true);
    try { await api(method, path, body); if (ok) toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const report = (id: string) => {
    const reason = window.prompt("Why should operations review this rating? (3+ characters)");
    if (reason) run("post", `/${side}/reviews/${id}/report`, { reason }, "Reported to operations");
  };
  if (error) return <p className="text-sm text-[var(--red)]">{error}</p>;
  if (!v) return <Loading />;
  const mine = side === "customer" ? v.customer_confirmed : v.vendor_confirmed;
  const other = side === "customer" ? "the provider" : "the customer";
  return (
    <div className="card space-y-3" data-testid={`rating-panel-${introId}`}>
      {title && <h3 className="font-bold">{title}</h3>}
      {!v.completed ? (
        <div className="space-y-2 text-sm">
          <p data-testid="rating-completion-status">{mine ? `You confirmed completion. Waiting for ${other} to confirm.` : `When the work is done, confirm it. Ratings open once you and ${other} both confirm.`}</p>
          {!mine && <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => run("post", `${base}/complete`, undefined, "Completion confirmed")} data-testid="rating-confirm-complete-button">Confirm work completed</button>}
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-[var(--navy)]" data-testid="rating-completion-status">Completed {fmt(v.completed_at)}</p>
          {v.my_review ? (
            <div className="flex flex-wrap items-center gap-3" data-testid="rating-my-review"><span className="font-semibold">Your rating:</span><StarRow value={v.my_review.stars} idPrefix="mine" />{v.my_review.status === "hidden" && <span className="badge">hidden by moderation</span>}</div>
          ) : (
            <div className="space-y-2">
              <p>Rate {other} (1–5 stars). Stars only, no written comments.</p>
              <StarRow value={pick} onPick={setPick} idPrefix={`rate-${introId}`} />
              <button className="btn btn-primary btn-sm" disabled={!pick || busy} onClick={() => run("post", `${base}/review`, { stars: pick }, "Rating saved")} data-testid="rating-submit-button">Submit rating</button>
            </div>
          )}
          <Theirs v={v} side={side} onReport={report} />
        </div>
      )}
    </div>
  );
}
